/* eslint-disable import/no-extraneous-dependencies */
import * as React from 'react';
import { InsuranceOffer } from 'types/Product';
import { Typography } from '@tvg/fuse/typography';
import { Radio } from '@tvg/fuse/radio';
import { RadioGroup } from '@tvg/fuse/radio-group';
import { getPriceWithSymbol } from 'utilities/price/price-utilities';
import getInsuranceMaxWeeklyPrice from 'utilities/insurance/insurance-utilities';
import { enableMonthlyProtectInsuranceTempOffer } from 'constants/promotion-voucher-constants';
import { getSite } from 'context/SiteContext';
import BrandTypes from 'constants/brand';
import styles from './InsuranceSelector.module.css';

type InsuranceRadioProps = React.ComponentProps<typeof Radio> & { radioLabel?: React.ReactNode };
function InsuranceRadio({ radioLabel, ...props }: InsuranceRadioProps) {
  // `@tvg/fuse/radio` renders `radioLabel` directly as children; types currently say string.
  return <Radio radioLabel={radioLabel as never} {...props} />;
}

interface InsuranceSelectorProps {
  options?: InsuranceOffer[] | null;
  selectedInsurance?: InsuranceOffer | string | null;
  handleSelectedInsurance: (option: InsuranceOffer | null) => void;
  handleNoInsuranceSelect: () => void;
  type?: string;
  showNoInsuranceRadio?: boolean;
  skuId: string;
}

export function InsuranceSelector({
  options,
  handleSelectedInsurance,
  handleNoInsuranceSelect,
  selectedInsurance,
  type = '',
  showNoInsuranceRadio = true,
  skuId,
}: InsuranceSelectorProps) {
  const site = getSite();
  const showTempMonthlyOfferText = site === BrandTypes.VERY && enableMonthlyProtectInsuranceTempOffer;
  const getChecked = (option: InsuranceOffer) => {
    if (typeof selectedInsurance === 'object' && !Object.is(selectedInsurance, null) && selectedInsurance?.offer) {
      return option.offer.productId === selectedInsurance?.offer.productId;
    }

    return false;
  };

  return options ? (
    <>
      <Typography className={styles.heading} color="var(--color-global-black)" size="1" tag="p">
        Choose an Insurance option
      </Typography>

      <RadioGroup dataTestId="insurance-radio-group" groupName={`insurance-radio-group${type}`}>
        <>
          {options.map((option) => {
            const maxWeeklyPrice = option?.offer.weeklyPrices?.length
              ? getInsuranceMaxWeeklyPrice(option.offer.weeklyPrices)
              : null;

            return (
              <InsuranceRadio
                className={[styles.radio, option.offer.termDescription === 'Monthly Insurance' ? 'monthly-radio' : '']
                  .filter(Boolean)
                  .join(' ')}
                dataTestId={`insurance-radio-button-${option.offer.termDescription
                  .toLocaleLowerCase()
                  .split(' ')
                  .join('-')}${type}`}
                clickCallback={() => {
                  handleSelectedInsurance(option);
                }}
                radioLabel={
                  <span className={styles.labelTextContainer}>
                    {option.offer.termDescription} £{option.offer.sellingPrice}
                    {option.offer.termDescription === 'Monthly Insurance' ? ' a month' : ''}
                    {site === BrandTypes.LITTLEWOODS && maxWeeklyPrice && (
                      <Typography size="m1" tag="span" type="paragraph">
                        or {getPriceWithSymbol(maxWeeklyPrice.weeklySellingPrice)} per week interest free over{' '}
                        {maxWeeklyPrice.numberOfWeeks} weeks
                      </Typography>
                    )}
                    {showTempMonthlyOfferText && option.offer.termDescription === 'Monthly Insurance' && (
                      <Typography color="var(--color-brand-mid)" size="m1" tag="span" type="paragraph">
                        First 6 months FREE*
                      </Typography>
                    )}
                  </span>
                }
                radioName={option.offer.termDescription}
                key={`${option.offer.productId}${type}${option.offer.sellingPrice}${skuId}`}
                checked={getChecked(option)}
              />
            );
          })}

          {showNoInsuranceRadio && (
            <InsuranceRadio
              className={styles.radio}
              dataTestId={`insurance-radio-button-no-insurance${type}`}
              clickCallback={handleNoInsuranceSelect}
              radioLabel="No Insurance"
              radioName="No Insurance"
              key="no-insurance-radio"
              checked={selectedInsurance === 'No insurance'}
            />
          )}
        </>
      </RadioGroup>
    </>
  ) : null;
}

export default { InsuranceSelector };
